import { useEffect, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  TechDocsAddonLocations,
  useTechDocsAddons,
} from '@backstage/plugin-techdocs-react';
import { TECHDOCS_HEADER_SLOT_ID } from '../layout/techDocsHeaderSlot';

const MENU_ID = 'techdocs-reader-header-settings';

/*
 * The reader's settings trigger, relocated into the portal header's own icon
 * cluster (next to Help and the notifications bell) rather than floating over
 * the article on its own.
 *
 * Vendor `TechDocsReaderPageSubheader` owns the real gear + menu, but it's not
 * composable at the button level and it's bound to `TechDocsReaderProvider`
 * context that only exists inside the reader's own routed page — outside the
 * reach of `PortalHeader`/`MobileTopBar`, which are mounted once at the shell
 * level. Rather than reach across that boundary, this addon fetches the same
 * Settings-location content the vendor gear shows (`useTechDocsAddons` is the
 * same public hook it calls) and renders its own trigger + menu — still fully
 * inside the reader's tree, so the content stays bound to the doc actually on
 * screen — then portals just the trigger + menu's DOM output into whichever
 * header slot is currently mounted. `readerChrome.css` hides the vendor gear,
 * which is now redundant.
 */
export const ReaderSettingsAddon = () => {
  const { renderComponentsByLocation } = useTechDocsAddons();
  const settingsAddons = renderComponentsByLocation(
    TechDocsAddonLocations.Settings,
  );
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Desktop and mobile render different chrome (`PortalHeader` vs
    // `MobileTopBar`), each mounting its own slot node under the same id, and
    // the shell swaps between them on the phone breakpoint — so the slot can
    // unmount and remount under this addon without it ever unmounting itself.
    // A body-wide observer is the only vantage point both producers share.
    const sync = () => setSlot(document.getElementById(TECHDOCS_HEADER_SLOT_ID));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (!settingsAddons || !slot) {
    return null;
  }

  const trigger = (
    <>
      <IconButton
        aria-controls={MENU_ID}
        aria-haspopup="true"
        title="Reader settings"
        onClick={(event: MouseEvent<HTMLElement>) =>
          setAnchor(event.currentTarget)
        }
      >
        <SettingsIcon />
      </IconButton>
      <Menu
        id={MENU_ID}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        keepMounted
      >
        <div>{settingsAddons}</div>
      </Menu>
    </>
  );

  return createPortal(trigger, slot);
};
